import React, {useEffect} from 'react';
import useIDEStore from '../zustand/useIDEStore';
import {ProjectVersion} from "../../UserCanister/candid/main.did";

// Format semantic version to string (e.g. "1.2.3-beta+001")
const formatVersion = (version: any) => {
    if (!version) return "Unknown";

    // Basic version number
    let result = `${version.major}.${version.minor}.${version.patch}`;

    // Only add prerelease if it exists and isn't empty
    if (version.prerelease && typeof version.prerelease === 'string' && version.prerelease.trim() !== '') {
        result += `-${version.prerelease}`;
    }

    // Only add build if it exists and isn't empty
    if (version.build && typeof version.build === 'string' && version.build.trim() !== '') {
        result += `+${version.build}`;
    }

    // Remove any "-+" pattern (empty prerelease followed by build)
    return result.replace('-+', '+');
};

interface VersionToolbarProps {
    onSuccess?: (message: string) => void;
    onError?: (message: string) => void;
}

const VersionToolbar: React.FC<VersionToolbarProps> = ({ onSuccess, onError }) => {
    // Get version-related state from store
    const projectVersions = useIDEStore(state => state.projectVersions || []) as ProjectVersion[];
    const currentVersionId = useIDEStore(state => state.currentVersionId);
    const isLoadingVersions = useIDEStore(state => state.isLoadingVersions);
    const switchToVersion = useIDEStore(state => state.switchToVersion);
    const refreshFiles = useIDEStore(state => state.refreshFiles);
    const project = useIDEStore(state => state.project);

    // Get the setActiveTab function from external state
    const setActiveTab = useIDEStore(state => state.setActiveTab);

    // Find current version object
    const currentVersion = projectVersions.find(v => v.id === currentVersionId);

    useEffect(() => {
        if (project && project.id !== 'none') {
            // Fetch project versions when the component mounts or project changes
            console.log('[VersionToolbar] Project changed, fetching versions');
            const fetchVersions = async () => {
                try {
                    await useIDEStore.getState().getProjectVersions();
                } catch (error) {
                    console.error('[VersionToolbar] Error fetching versions:', error);
                }
            };

            fetchVersions();
        }
    }, [project?.id]);

    // Custom styles to match your existing UI
    const buttonStyle = {
        backgroundColor: "#8B4513",
        color: "#FFF5EE",
        padding: "4px 10px",
        border: "none",
        borderRadius: "4px",
        cursor: "pointer",
        fontWeight: "500",
        fontSize: "13px"
    };

    const versionBadgeStyle = (status: string) => {
        // Base style
        const style = {
            fontSize: "12px",
            padding: "2px 8px",
            borderRadius: "10px",
            marginLeft: "8px",
            fontWeight: "500",
        };

        // Status-specific colors - use string comparisons rather than enum values
        switch (status) {
            case 'released':
                return { ...style, backgroundColor: "#e2f1e8", color: "#2d6a4f" };
            case 'development':
                return { ...style, backgroundColor: "#e3f2fd", color: "#1976d2" };
            case 'deprecated':
                return { ...style, backgroundColor: "#ffebee", color: "#c62828" };
            case 'draft':
            default:
                return { ...style, backgroundColor: "#f3f4f6", color: "#6b7280" };
        }
    };

    const containerStyle = {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "6px 15px",
        backgroundColor: "#fdf6e3",
        borderBottom: "1px solid #8B4513"
    };

    const selectStyle = {
        padding: "4px 8px",
        borderRadius: "4px",
        border: "1px solid #8B4513",
        backgroundColor: "#fff",
        fontSize: "13px",
        marginRight: "5px"
    };

    // Handle switching to current files (no version)
    const handleUseCurrentFiles = () => {
        console.log("[INFO] Switching to current working files mode");

        // Set currentVersionId to null in the store
        useIDEStore.setState({ currentVersionId: null });

        // Explicitly refresh files with null versionId parameter to ensure
        // we're using the "current files" logic branch
        refreshFiles(null);

        if (onSuccess) {
            onSuccess("Switched to current working files");
        }
    };

    // Handler for version selection
    const handleVersionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const selectedValue = e.target.value;

        if (selectedValue === "current") {
            handleUseCurrentFiles();
        } else {
            console.log(`[INFO] Switching to version: ${selectedValue}`);
            switchToVersion(selectedValue);
        }
    };

    // Switch to versions tab instead of opening dialog
    const handleManageVersions = () => {
        // Use the setActiveTab function from the store to switch tabs
        setActiveTab('versions');

        if (onSuccess) {
            onSuccess("Opened versions management");
        }
    };

    if (!project || project.id === 'none') {
        return null; // Don't show version toolbar if no project is selected
    }

    // Helper to get status display text and style
    const getStatusDisplay = (version: any) => {
        if (!version || !version.status) return null;

        // Handle both string and variant types
        let statusText: string;

        if (typeof version.status === 'string') {
            statusText = version.status;
        } else if (typeof version.status === 'object') {
            // Extract the key name from the object - assuming it's a variant type
            const keys = Object.keys(version.status);
            statusText = keys.length > 0 ? keys[0] : 'unknown';
        } else {
            return null;
        }

        // Base style
        const baseStyle = {
            fontSize: "12px",
            padding: "2px 8px",
            borderRadius: "10px",
            marginLeft: "8px",
            fontWeight: "500" as const,
        };

        // Just use a simple switch with string comparison (case insensitive)
        let bgColor = "#f3f4f6";
        let textColor = "#6b7280";
        const lowerStatus = statusText.toLowerCase();

        switch (lowerStatus) {
            case 'released':
                bgColor = "#e2f1e8";
                textColor = "#2d6a4f";
                break;
            case 'development':
                bgColor = "#e3f2fd";
                textColor = "#1976d2";
                break;
            case 'deprecated':
                bgColor = "#ffebee";
                textColor = "#c62828";
                break;
        }

        return (
            <span style={{ ...baseStyle, backgroundColor: bgColor, color: textColor }}>
                {statusText}
            </span>
        );
    };

    return (
        <div style={containerStyle}>
            <div style={{ display: "flex", alignItems: "center" }}>
                <span style={{ fontSize: "14px", color: "#5C4033", marginRight: "10px" }}>Version:</span>
                {isLoadingVersions ? (
                    <div style={{
                        width: "120px",
                        height: "26px",
                        backgroundColor: "#e5e7eb",
                        borderRadius: "4px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center"
                    }}>
                        <span style={{ fontSize: "12px", color: "#6b7280" }}>Loading...</span>
                    </div>
                ) : projectVersions.length > 0 ? (
                    <div style={{display: "flex", alignItems: "center"}}>
                        <select
                            value={currentVersionId || "current"}
                            onChange={handleVersionChange}
                            style={selectStyle}
                        >
                            <option value="current">Current Files (No Version)</option>
                            {projectVersions.map(version => (
                                <option key={version.id} value={version.id}>
                                    {formatVersion(version.semanticVersion)}
                                </option>
                            ))}
                        </select>
                        {currentVersion && getStatusDisplay(currentVersion)}
                        {!currentVersion && !currentVersionId && (
                            <span style={versionBadgeStyle("development")}>
                                Working Copy
                            </span>
                        )}
                    </div>
                ) : (
                    <div style={{display: "flex", alignItems: "center"}}>
                        <span style={{fontSize: "14px", color: "#8B4513", fontStyle: "italic", marginRight: "10px"}}>
                            No versions found
                        </span>
                        <span style={versionBadgeStyle("development")}>
                            Working Copy
                        </span>
                    </div>
                )}
            </div>

            <div>
                <button
                    onClick={handleManageVersions}
                    style={buttonStyle}
                >
                    Manage Versions
                </button>
            </div>
        </div>
    );
};

export default VersionToolbar;